/* eslint-disable max-lines */
import { OrganisationRole } from '@/store/types';
import { TokenService } from '@/services/TokenService';
import { createRouter, createWebHistory } from 'vue-router';
import { authenticationStore } from './store/pinia/AuthenticationStore';
import { tableStore } from './store/pinia/ExtendedDataTableStore';
import { onPremises } from './shared/utils';

const routes = [
  {
    path: '/',
    redirect: '/dashboard'
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/page-not-found'
  },
  {
    path: '/page-not-found',
    name: 'error404',
    component: () => import('./views/information/PageNotFound.vue'),
    meta: {
      unprotected: true
    }
  },
  {
    path: '/plan-overview',
    name: 'plan-overview',
    component: () => import('./views/information/PlanOverview.vue'),
    meta: {
      visibleForUnlicensed: true,
      onlyIfCloud: true
    }
  },
  {
    path: '/data-processing-policy',
    name: 'data-processing-policy',
    component: () => import('./views/information/PrivacyInformation.vue'),
    meta: {
      unprotected: true,
      visibleForUnlicensed: true
    }
  },
  {
    path: '/500',
    name: 'error500',
    component: () => import('./views/information/Error500.vue'),
    meta: {
      unprotected: true
    }
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('./views/Dashboard.vue')
  },
  {
    path: '/download-area',
    name: 'download-area',
    component: () => import('./views/information/DownloadArea.vue'),
    meta: { onlyIfCloud: true, visibleForUnlicensed: true }
  },
  {
    path: '/certificate-authorities',
    name: 'certificate-authorities',
    component: () => import('./views/CertificateAuthorities.vue'),
    meta: {
      proOnly: true
    }
  },
  {
    path: '/certificates',
    name: 'certificates',
    component: () => import('./views/Certificates.vue'),
    meta: {
      proOnly: true
    }
  },
  {
    path: '/devices',
    name: 'devices',
    component: () => import('./views/Devices.vue'),
    children: []
  },
  {
    path: '/devices/:uuid',
    name: 'device-details',
    component: () => import('./views/details/DeviceDetails.vue')
  },
  {
    path: '/templates',
    name: 'templates',
    component: () => import('./views/AsciiTemplates.vue'),
    meta: {
      proOnly: true
    }
  },
  {
    path: '/templates/copy',
    name: 'templates-copy',
    component: () => import('./views/details/CopyAsciiTemplate.vue'),
    meta: {
      cliTemplatesEnabled: true
    }
  },
  {
    path: '/templates/:id',
    name: 'templates-details-by-id',
    component: () => import('./views/details/AsciiTemplateDetails.vue')
  },
  {
    path: '/update-server',
    name: 'update-server',
    component: () => import('./views/update/AutoUpdateServer.vue'),
    meta: {
      autoUpdateServerEnabled: true
    }
  },
  {
    path: '/smart-update',
    name: 'smart-update',
    component: () => import('./views/update/SmartUpdate.vue'),
    meta: { onlyIfCloud: true, proOnly: true }
  },
  {
    path: '/quickstart-wizard/:step',
    name: 'quickstart',
    component: () => import('./views/QuickstartWizard.vue'),
    meta: {
      onlyIfCloud: true
    }
  },
  {
    path: '/update-server/:uuid',
    name: 'update-server-details',
    component: () => import('./views/details/AutoUpdateServerDetails.vue'),
    meta: {
      autoUpdateServerEnabled: true
    }
  },
  {
    path: '/update-jobs',
    name: 'update-jobs',
    component: () => import('./views/update/UpdateJobs.vue'),
    meta: {
      updateJobsEnabled: true
    }
  },
  {
    path: '/update-jobs/:id',
    name: 'update-jobs-details',
    component: () => import('./views/details/UpdateJobDetails.vue')
  },
  {
    path: '/update-packages',
    name: 'update-packages',
    component: () => import('./views/update/UpdatePackets.vue'),
    meta: {
      proOnly: true
    }
  },
  {
    path: '/firmware',
    name: 'firmware',
    component: () => import('./views/Firmware.vue')
  },
  {
    path: '/files',
    name: 'files',
    component: () => import('./views/update/Resources.vue'),
    meta: {
      proOnly: true
    }
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('./views/Login.vue'),
    meta: {
      unprotected: true,
      onlyIfLoggedOut: true
    }
  },
  {
    path: '/auth/oidc-callback',
    name: 'OAuthCallback',
    component: () => import('./views/OAuthCallback.vue'),
    meta: {
      unprotected: true
    }
  },
  {
    path: '/signup',
    name: 'signup',
    component: () => import('./views/Signup.vue'),
    meta: {
      unprotected: true,
      onlyIfLoggedOut: true,
      onlyIfCloud: true
    }
  },
  {
    path: '/signup/:invitationCode',
    name: 'signupWithInvitationCode',
    component: () => import('./views/SignupWithInvitationCode.vue'),
    meta: {
      unprotected: true,
      onlyIfLoggedOut: false
    }
  },
  {
    path: '/confirm/:confirmationCode',
    name: 'confirmation',
    component: () => import('./views/Confirmation.vue'),
    meta: {
      unprotected: true,
      onlyIfLoggedOut: false
    }
  },
  {
    path: '/reset-password/:resetPasswordCode',
    name: 'resetPassword',
    component: () => import('./views/ResetPassword.vue'),
    meta: {
      unprotected: true,
      onlyIfLoggedOut: true
    }
  },
  {
    path: '/device-groups',
    name: 'device-groups',
    component: () => import('./views/DeviceGroups.vue')
  },

  {
    path: '/settings',
    name: 'settings',
    component: () => import('./views/userMenu/Settings.vue'),
    meta: {
      visibleForUnlicensed: true
    }
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import('./views/userMenu/UserProfile.vue'),
    meta: {
      visibleForUnlicensed: true
    }
  },
  {
    path: '/account',
    name: 'account',
    component: () => import('./views/userMenu/AccountInformation.vue'),
    meta: {
      visibleForUnlicensed: true
    }
  },
  {
    path: '/licenses',
    name: 'licenses',
    component: () => import('./views/userMenu/Licenses.vue'),
    meta: {
      visibleForUnlicensed: true
    }
  },
  {
    path: '/users',
    name: 'account-users',
    component: () => import('./views/userMenu/AccountUsers.vue'),
    meta: {
      visibleForUnlicensed: true,
      onlyIfCloud: true
    }
  },
  {
    path: '/audit-logs',
    name: 'audit-logs',
    component: () => import('./views/AuditLogs.vue')
  },
  {
    path: '/administration',
    name: 'administration',
    redirect: '/administration/system-information',
    meta: {
      onlyIfNotJustCustomer: true
    },
    children: [
      {
        path: 'system-information',
        meta: {
          onlyIfNotJustCustomer: true
        },
        component: () => import('./views/administration/SystemInformation.vue')
      },
      {
        path: 'user-and-account-management',
        meta: {
          onlyIfNotJustCustomer: true,
          userManagementEnabled: true
        },
        redirect: '/administration/user-and-account-management/accounts',
        children: [
          {
            path: 'firmware-management',
            meta: {
              onlyIfNotJustCustomer: true
            },
            props: true,
            component: () =>
              import('./views/administration/FirmwareManagement.vue')
          },
          {
            path: 'accounts',
            meta: {
              onlyIfNotJustCustomer: true
            },
            props: true,
            component: () => import('./views/administration/Accounts.vue')
          },
          {
            path: 'users',
            meta: {
              onlyIfNotJustCustomer: true
            },
            props: true,
            component: () => import('./views/administration/Users.vue')
          },
          {
            path: 'organisations',
            meta: {
              onlyIfNotJustCustomer: true
            },
            props: true,
            component: () => import('./views/administration/Organisations.vue')
          }
        ]
      },
      {
        path: 'system-settings',
        meta: {
          onlyIfNotJustCustomer: true
        },
        component: () => import('./views/administration/SystemSettings.vue')
      }
    ]
  }
];

// there is also createWebHashHistory and createMemoryHistory

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes
});

const changingMainPath = (toPath: string, fromPath: string): boolean =>
  toPath.split('/')[1] !== fromPath.split('/')[1];

router.beforeEach(async (to, from) => {
  if (
    changingMainPath(to.fullPath, from.fullPath) &&
    from.name !== 'dashboard'
  ) {
    tableStore().clearStore();
  }
  const isUnprotected = to.matched.some((record) => record.meta.unprotected);

  const onlyIfLoggedOut = to.matched.some(
    (record) => record.meta.onlyIfLoggedOut
  );
  const pathNotForCustomer = to.matched.some(
    (record) => record.meta.onlyIfNotJustCustomer
  );
  const onlyIfCloud = to.matched.some((record) => record.meta.onlyIfCloud);

  const visibleForUnlicensed = to.matched.some(
    (record) => record.meta.visibleForUnlicensed
  );

  const refreshToken = await TokenService.getRefreshToken();

  const loggedIn = !!refreshToken;
  const authStore = authenticationStore();

  const licensedFeatures =
    loggedIn && authStore.licensedFeatures ? authStore.licensedFeatures : {};
  const organisationRoles =
    loggedIn && authStore.loggedInUser?.organisation?.roles
      ? authStore.loggedInUser.organisation.roles
      : [];
  const onpremises = onPremises();
  const featureFlagMeta = Object.keys(licensedFeatures).reduce(
    (acc, featureFlag) =>
      Object.assign(acc, {
        [featureFlag]: to.matched.some((record) => record.meta[featureFlag])
      }),
    {}
  );

  if (loggedIn && authStore.isUnlicensedAccount && !visibleForUnlicensed) {
    return '/plan-overview';
  }

  const featureFlagBlock = Object.entries(featureFlagMeta).find(
    ([featureFlag, meta]) => meta && !licensedFeatures[featureFlag]
  );

  // Route requires a licensed feature that is not available.
  if (featureFlagBlock) {
    return '/';
  }

  // Protected routes require authentication. Keep intended target for post-login redirect.
  if (!isUnprotected && !loggedIn && from.path !== '/login') {
    return {
      path: '/login',
      query: { redirect: to.fullPath }
    };
  }

  // Logged-in users should not open auth-only routes like login/signup/reset password.
  if (loggedIn && onlyIfLoggedOut) {
    return '/';
  }

  // On-prem mode blocks cloud-only routes.
  if (onpremises && onlyIfCloud) {
    if (!loggedIn) {
      return '/login';
    }

    return '/';
  }

  if (
    pathNotForCustomer &&
    !organisationRoles.find(
      (role: string) =>
        role === OrganisationRole.SUPPLIER ||
        role === OrganisationRole.BROKER ||
        role === OrganisationRole.RESELLER ||
        role === OrganisationRole.PLATFORM_OPERATOR
    )
  ) {
    // Customer-only users cannot access routes marked onlyIfNotJustCustomer.
    return '/';
  }

  return;
});

export default router;
